import React, { useState, useEffect, useMemo } from "react";
import { SkeletonLoader, colors2, ButtonGhostCompact } from "oolib";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  UI_BODY_SM,
  UI_TAG,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_CAPTION,
  UI_CAPTION_SEMIBOLD_DF,
  UI_TAG_DF,
} from "oolib/dist/v2/components/Typo2";
import { DashboardWidgetShell } from "../../comps/DashboardWidgetShell";
import { PillDropdown } from "../../comps/PillDropdown";
import { SidePanel } from "@/components/UI/SidePanel";
import { MentionCard } from "../../comps/MentionCard";
import { LineChart } from "@/components/dataviz/LineChartV2";
import { TagDetailSidebar } from "../comps/TagDetailSidebar";
import { useGetSignalsLens, useGetSignalDetail, useGetSignalThemeDetail } from "../../hooks/useGetLensData";
import { StatusPill } from "../../comps/StatusPill";
import { WIDGET_STYLE, WIDGET_BODY_STYLE, TREND_THEME } from "../constants";
import { Lightning, Signpost } from "@phosphor-icons/react";

// Same palette as the V2 signal categories (signalCategories.js / SignalsWidget CONFIG).
const BADGE_COLORS = {
  user_dissatisfaction: { bg: "#D4553A", color: colors2.white, accent: "#D4553A" },
  user_behavior:       { bg: "#D9A02A", color: colors2.white, accent: "#D9A02A" },
  user_satisfaction:   { bg: "#2FA075", color: colors2.white, accent: "#2FA075" },
  user_needs:          { bg: "#8F55B3", color: colors2.white, accent: "#8F55B3" },
};
const DEFAULT_BADGE = { bg: colors2.grey40, color: colors2.grey80 };

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const SignalList = styled.div`
  display: flex;
  flex-direction: column;
`;

const SignalCard = styled.div`
  padding: 1.2rem 0.5rem;
  border-bottom: 1px solid ${colors2.grey5};
  border-radius: 0.6rem;
  cursor: pointer;
  border: 2px solid ${({ $selected }) => $selected ? colors2.grey30 : "transparent"};
  background: ${({ $selected }) => $selected ? colors2.grey2 : "transparent"};
  &:hover { background: ${colors2.grey2}; }
  &:last-child { border-bottom: none; }
`;

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.6rem;
`;

const SignalBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.625rem;
  border-radius: 999px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const MentionCount = styled(UI_CAPTION_SEMIBOLD_DF)`
  color: ${colors2.grey40};
`;

const TrendLabel = styled(UI_CAPTION)`
  color: ${(p) => p.$color};
  font-weight: 600;
`;

const ContextRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-left: 1rem;
`;

const ContextTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 4px;
  background: ${colors2.grey5};
  color: ${colors2.grey60};
`;

// ── Panel 2 section styles ──────────────────────────────────

const Section = styled.div`
  padding: 14px;
  border-bottom: 1px solid ${colors2.grey10};
  &:last-child { border-bottom: none; }
`;

const SectionLabel = styled(UI_TAG)`
  color: ${colors2.grey50};
  display: block;
  margin-bottom: 0.6rem;
`;

const ThemeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  cursor: pointer;
  border-bottom: 1px solid ${colors2.grey5};
  &:hover { background: ${colors2.grey2}; }
  &:last-child { border-bottom: none; }
`;

const FilterText = styled(UI_CAPTION)`
  color: ${colors2.grey50};
`;

// Dissatisfaction declining = good (green), rising = bad (red). Satisfaction is the opposite.
const NEGATIVE_CATEGORIES = new Set(["user_dissatisfaction"]);
function getSignalTrendTheme(trend, intentGroup) {
  if (trend === "new") return TREND_THEME.new;
  if (trend === "stable") return TREND_THEME.stable;
  const isNegative = NEGATIVE_CATEGORIES.has(intentGroup);
  // For negative signals: rising=bad(red theme), declining=good(green theme) — matches default TREND_THEME
  // For positive signals: flip — rising=good(green), declining=bad(red)
  if (trend === "rising") return isNegative ? TREND_THEME.rising : TREND_THEME.declining;
  if (trend === "declining") return isNegative ? TREND_THEME.declining : TREND_THEME.rising;
  return TREND_THEME.stable;
}

// VIEW_OPTIONS removed — only "all signals" view is active
// const VIEW_OPTIONS = [
//   { value: "all", display: "All signals" },
//   { value: "trending", display: "Top trending Signals" },
//   { value: "new", display: "New signals" },
// ];

const ALL_TYPES_OPTION = { value: "all", display: "All Signals" };

function toTitleCase(s = "") {
  return s.replace(/^user_/, "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function SignalsLensBlock({ period, locality, meta }) {
  const { data, isLoading } = useGetSignalsLens({ period, locality });
  // jumps to the full Signals explorer, now the Signals sub-view under Highlights
  const navigate = useNavigate();
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);
  // view state removed — only "all signals" behaviour
  // const [view, setView] = useState(VIEW_OPTIONS[0]);
  const [typeFilter, setTypeFilter] = useState(ALL_TYPES_OPTION);

  // Type options from meta.signalCategories (tag.category field, no LLM)
  // Old: derived dynamically from data?.signals intentGroup (LLM-classified)
  // const typeOptions = React.useMemo(() => {
  //   const groups = new Set((data?.signals || []).map((s) => s.intentGroup).filter(Boolean));
  //   return [ALL_TYPES_OPTION, ...Array.from(groups).map((g) => ({ value: g, display: toTitleCase(g) }))];
  // }, [data?.signals]);
  const typeOptions = [
    ALL_TYPES_OPTION,
    ...(meta?.signalCategories || []).map((c) => ({ value: c.value, display: c.display })),
  ];

  // Panel 2 data
  const { data: signalDetail, isLoading: detailLoading } = useGetSignalDetail({
    signalTag: selectedSignal?.signalTag,
    period,
    locality,
    enabled: !!selectedSignal,
  });

  // Panel 3 data
  const { data: themeDetail, isLoading: themeLoading } = useGetSignalThemeDetail({
    signalTag: selectedSignal?.signalTag,
    themeId: selectedTheme?.id,
    themeType: selectedTheme?.type,
    period,
    enabled: !!selectedSignal && !!selectedTheme,
  });

  useEffect(() => {
    setSelectedSignal(null);
    setSelectedTheme(null);
  }, [period]);

  const allSignals = data?.signals || [];

  // Apply filters — only type filter active; view dropdown removed
  let signals = allSignals;
  if (typeFilter.value !== "all") {
    signals = signals.filter((s) => s.intentGroup === typeFilter.value);
  }
  // Trending/new view filtering commented out — revisit when view dropdown returns
  // if (view.value === "trending") { signals = signals.filter((s) => s.trend === "rising" || s.trend === "new"); }
  // else if (view.value === "new") { signals = signals.filter((s) => s.trend === "new"); }
  signals = [...signals].sort((a, b) => b.mentionCount - a.mentionCount);

  // Build line chart data from filtered signals' sparklines
  const chartData = useMemo(() => {
    const chartSignals = signals.filter((s) => s.sparkline?.length > 1);
    if (chartSignals.length === 0) return null;

    const template = chartSignals[0].sparkline;
    if (template.length < 2) return null;

    const colorMap = {};
    for (const sig of chartSignals) {
      const badge = BADGE_COLORS[sig.intentGroup] || DEFAULT_BADGE;
      colorMap[sig.display] = badge.accent || badge.bg;
    }

    const rows = template.map((bucket) => {
      const row = { label: bucket.label };
      for (const sig of chartSignals) {
        const point = sig.sparkline?.find((s) => s.index === bucket.index);
        row[sig.display] = point?.count || 0;
      }
      return row;
    });

    return { rows, valuePaths: chartSignals.map((s) => s.display), colorMap };
  }, [signals]);

  const handleClosePanel2 = () => {
    setSelectedSignal(null);
    setSelectedTheme(null);
  };

  const signalAccent = (BADGE_COLORS[selectedSignal?.intentGroup] || DEFAULT_BADGE).accent || colors2.grey80;

  const selectedThemeFinding = (signalDetail?.findings || []).find((f) => f.themeId === selectedTheme?.id);

  return (
    <>
      <DashboardWidgetShell
        titleJSX={
          <TitleWrapper>
            <Lightning size={16} color={colors2.grey80} weight="bold" />
            <UI_BODY_SEMIBOLD_SM_DF>Signals</UI_BODY_SEMIBOLD_SM_DF>
            {/* <PillDropdown value={typeFilter} onChange={(_, val) => setTypeFilter(val)} options={typeOptions} /> */}
          </TitleWrapper>
        }
        headerRight={
          <ButtonGhostCompact
            S
            icon="ArrowRight"
            value="Open in Signals Explorer"
            onClick={() => navigate("/analysis/highlights?highlightsView=signals")}
          />
        }
        style={WIDGET_STYLE}
        bodyStyle={WIDGET_BODY_STYLE}
      >
        {isLoading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} style={{ padding: "8px 0" }}>
                <SkeletonLoader style={{ height: 22, width: 80, borderRadius: 12, marginBottom: 8 }} />
                <SkeletonLoader style={{ height: 12, width: "90%", borderRadius: 4, marginBottom: 4 }} />
                <SkeletonLoader style={{ height: 12, width: "70%", borderRadius: 4 }} />
              </div>
            ))}
          </div>
        )}
        {!isLoading && signals.length === 0 && (
          <UI_BODY_SM style={{ color: colors2.grey40 }}>No signal data available yet.</UI_BODY_SM>
        )}
        {!isLoading && allSignals.length > 0 && (
          <>
            {chartData && (
              <div style={{ marginBottom: "0.5rem", position: "sticky", top: 0, zIndex: 2, background: colors2.white }}>
                <LineChart
                  data={chartData.rows}
                  valuePath={chartData.valuePaths}
                  xAxisPath="label"
                  height={240}
                  hideYAxis
                  hideXAxisLine
                  smartTicks={false}
                  colors={chartData.colorMap}
                  defaultOpacity={0.4}
                  highlightLine={selectedSignal?.display || null}
                  margin={{ top: 10, right: 10, bottom: 30, left: 10 }}
                  style={{ minHeight: 240, height: 240 }}
                />
              </div>
            )}
            <SignalList>
              {signals.length === 0 && (
                <UI_BODY_SM style={{ color: colors2.grey40, padding: "1rem 0" }}>No signals match this filter.</UI_BODY_SM>
              )}
              {signals.map((signal, idx) => {
                const badge = BADGE_COLORS[signal.intentGroup] || DEFAULT_BADGE;
                const trendTheme = getSignalTrendTheme(signal.trend, signal.intentGroup);

                return (
                  <SignalCard key={signal.signalTag} $selected={selectedSignal?.signalTag === signal.signalTag} onClick={() => { setSelectedSignal(signal); setSelectedTheme(null); }}>
                    <BadgeRow>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", width: "100%", alignItems: "center" }}>
                      <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                      <UI_CAPTION style={{ color: colors2.grey40 }}>{idx + 1}</UI_CAPTION>
                      <SignalBadge $bg={badge.bg} $color={badge.color}>
                        <UI_TAG style={{ color: "inherit" }}>{signal.display}</UI_TAG>
                      </SignalBadge>
                        <MentionCount>{signal.mentionCount} mentions</MentionCount>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <StatusPill theme={trendTheme} iconSize={14} />
                      </div>
                      </div>
                    </BadgeRow>
                    {signal.contextTags?.length > 0 && (
                      <ContextRow>
                        {signal.contextTags.slice(0, 4).map((ct) => (
                          <ContextTag key={ct.id}>
                            <UI_TAG>{ct.display}</UI_TAG>
                          </ContextTag>
                        ))}
                      </ContextRow>
                    )}
                  </SignalCard>
                );
              })}
            </SignalList>
          </>
        )}
      </DashboardWidgetShell>

      {/* ── Panel 2: Signal detail sidebar ── */}
      <TagDetailSidebar
        isOpen={!!selectedSignal}
        onClose={handleClosePanel2}
        isLoading={detailLoading}
        width={400}
        rightOffset={selectedTheme ? 380 : 0}
        closeOnOutsideClick={!selectedTheme}
        title={selectedSignal?.display}
        overline={selectedSignal?.intentGroup ? toTitleCase(selectedSignal.intentGroup) : undefined}
        mentionsCount={selectedSignal?.mentionCount}
        description={selectedSignal?.description}
        sources={signalDetail?.sources}
        // mentions={(signalDetail?.findings || [])
        //   .filter((f) => f.text)
        //   .map((f) => ({ text: f.text, tone: "neutral" }))}
        primaryColor={signalAccent}
        // glowColor={(BADGE_COLORS[selectedSignal?.intentGroup] || DEFAULT_BADGE).bg}
        overrideHeaderTitle={
          <TitleWrapper>
            <Signpost size={16} color={signalAccent} weight="bold" />
            <UI_TAG_DF style={{ color: signalAccent }}>SIGNAL</UI_TAG_DF>
          </TitleWrapper>
        }
        themes={signalDetail?.themes}
        onThemeBarClick={(theme) => setSelectedTheme(theme)}
        findings={signalDetail?.findings}
        onFindingClick={(theme) => setSelectedTheme(theme)}
      />

      {/* Original Panel 2 SidePanel — kept for the themes/findings drill-down
          into Panel 3, not yet ported to TagDetailSidebar.
      <SidePanel
        isOpen={!!selectedSignal}
        onClose={handleClosePanel2}
        title={selectedSignal?.display || ""}
        width={400}
        rightOffset={selectedTheme ? 380 : 0}
        closeOnOutsideClick={!selectedTheme}
        bodyStyle={{ padding: 0 }}
      >
        {detailLoading ? (
          <Section><UI_CAPTION>Loading...</UI_CAPTION></Section>
        ) : signalDetail ? (
          <>
            {selectedSignal?.description && (
              <Section>
                <UI_BODY_SM style={{ color: colors2.grey80, lineHeight: 1.6 }}>{selectedSignal.description}</UI_BODY_SM>
              </Section>
            )}
            <Section>
              <SectionLabel>SOURCES</SectionLabel>
              {(signalDetail.sources || []).map((s) => (
                <UI_BODY_SM key={s.contentType} style={{ color: colors2.grey70, marginBottom: 4 }}>
                  {s.count} {s.display}
                </UI_BODY_SM>
              ))}
            </Section>

            {signalDetail.themes?.length > 0 && (
              <Section>
                <SectionLabel>THEMES</SectionLabel>
                {signalDetail.themes.map((theme) => (
                  <ThemeRow key={theme.id} onClick={() => setSelectedTheme(theme)}>
                    <UI_BODY_SM style={{ color: colors2.grey80 }}>{theme.display}</UI_BODY_SM>
                    <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey50 }}>{theme.count}</UI_CAPTION_SEMIBOLD_DF>
                  </ThemeRow>
                ))}
              </Section>
            )}

            {signalDetail.findings?.length > 0 && (
              <Section>
                <SectionLabel>FINDINGS IN THIS SIGNAL</SectionLabel>
                {signalDetail.findings.map((finding) => finding.text && (
                  <div
                    key={finding.themeId}
                    onClick={() => setSelectedTheme({ id: finding.themeId, display: finding.themeDisplay, type: finding.themeType })}
                    style={{ padding: "0.75rem 0", borderBottom: "1px solid", cursor: "pointer" }}
                  >
                    <UI_BODY_SM style={{ color: colors2.grey80, lineHeight: 1.6 }}>{finding.text}</UI_BODY_SM>
                  </div>
                ))}
              </Section>
            )}
          </>
        ) : null}
      </SidePanel>
      */}

      {/* ── Panel 3: Signal × Theme finding sidebar ── */}
      <TagDetailSidebar
        isOpen={!!selectedTheme}
        onClose={() => setSelectedTheme(null)}
        isLoading={themeLoading}
        width={380}
        title={selectedTheme ? `${selectedSignal?.display} in ${selectedTheme.display}` : ""}
        primaryColor={signalAccent}
        mentionsCount={themeDetail?.mentionCount}
        description={selectedThemeFinding?.text}
        sources={themeDetail?.sources}
        mentions={(themeDetail?.annotations || []).map((a) => ({ text: a.text, tone: a.sentiment }))}
      />

      {/* Original Panel 3 SidePanel — kept for reference, props ported to
          TagDetailSidebar one by one (zIndex not yet supported).
      <SidePanel
        isOpen={!!selectedTheme}
        onClose={() => setSelectedTheme(null)}
        title={selectedTheme ? `${selectedSignal?.display} in ${selectedTheme.display}` : ""}
        width={380}
        zIndex={1001}
        bodyStyle={{ padding: 0 }}
      >
        {themeLoading ? (
          <Section><UI_CAPTION>Loading...</UI_CAPTION></Section>
        ) : themeDetail ? (
          <>
            {(() => {
              const finding = (signalDetail?.findings || []).find((f) => f.themeId === selectedTheme?.id);
              return finding?.text ? (
                <Section>
                  <UI_BODY_SM style={{ color: colors2.grey80, lineHeight: 1.6 }}>{finding.text}</UI_BODY_SM>
                </Section>
              ) : null;
            })()}

            <Section>
              <UI_BODY_SM style={{ color: colors2.grey70 }}>
                {themeDetail.mentionCount} mentions this month
              </UI_BODY_SM>
            </Section>

            <Section>
              <SectionLabel>SOURCES</SectionLabel>
              {(themeDetail.sources || []).map((s) => (
                <UI_BODY_SM key={s.contentType} style={{ color: colors2.grey70, marginBottom: 4 }}>
                  {s.count} {s.display}
                </UI_BODY_SM>
              ))}
            </Section>

            {themeDetail.annotations?.length > 0 && (
              <Section>
                <SectionLabel>MENTIONS</SectionLabel>
                {themeDetail.annotations.map((a, i) => (
                  <MentionCard key={a.id || i} tone={a.sentiment}>{a.text}</MentionCard>
                ))}
              </Section>
            )}
          </>
        ) : null}
      </SidePanel>
      */}
    </>
  );
}
